import type { Visit } from '.';
import type { PetSpecies } from '../buruburu/pet';
import { PublicProfile } from '../profile/profile.state';
import { getLocale } from '../getters/user';
import { getDynamicReceiverBucket } from '../getters/ab';
import ab from '../ruleset/ab';
import { defineAsyncGetters } from '../utils';
import type { PetState } from '../buruburu/pet/pet.ruleset';

export const visitAsyncGetters = (
  Visit: Visit,
  { Pet, VisitRecord }: Visit['$DEPENDENCIES'],
) => {
  function sortFriends(now: number, a: Visit.State, b: Visit.State) {
    const order =
      Pet.getters(b).getStressValue(now) - Pet.getters(a).getStressValue(now);
    if (order !== 0) {
      return order;
    }

    return b.updatedAt - a.updatedAt;
  }

  return defineAsyncGetters<Visit>()({
    /**
     * Get all attribute values of friend
     */
    getFriendAttributeValues: async (playerId: string, api) => {
      const statesMap = await api.fetchStates([playerId]);
      const playerState = statesMap[playerId];

      if (!playerState) {
        return null;
      }

      const now = Math.floor(api.date.now() / 1000) * 1000;

      return Pet.getters(playerState.state).getAllAttributeValues(now);
    },

    getPetVisitDetails: async (playerId: string, api) => {
      const statesMap = await api.fetchStates([playerId]);
      const playerState = statesMap[playerId];
      if (!playerState) {
        return null;
      }

      const { state } = playerState;
      if (!state.publicProfile) {
        const ownState = await api.getOwnState();
        if (!Object.keys(ownState.friends).includes(playerId)) {
          throw new Error(`Cannot visit a non friend without a public profile`);
        }
      }

      const {
        getAllAttributeValues,
        getCurrentEvolution,
        getPetAge,
        getStressValue,
        getAllStatusEffects,
        getPetState,
      } = Pet.getters(state);
      const currentEvolution = getCurrentEvolution();
      if (!currentEvolution?.species) {
        return null;
      }

      const now = api.date.now();
      const { current: level, xp } = state.level;
      const { name } = state.publicProfile ?? state.profile;

      return {
        isOnline: state.isOnline,
        name: name ?? 'unknown',
        locale: getLocale(state),
        level,
        xp,
        currentEvolution,
        age: getPetAge(now),
        answers: state.conversation.answers,
        statusEffects: { ...getAllStatusEffects(now), evolving: false },
        state: getPetState(now, true),
        notificationSettings: state.settings.receiveMessages,
        attributes: {
          name: state.pet.name,
          ...getAllAttributeValues(now),
          stress: getStressValue(now),
        },
      };
    },

    getVisitFriendsList: async (limitTo: number, api) => {
      const now = api.date.now();
      const fetchFriendListData = async (
        ids: string[],
      ): Promise<FriendListData[]> => {
        const states = await api.fetchStates(ids);
        return Object.values(states)
          .sort((a, b) => sortFriends(now, a.state, b.state))
          .map(({ state }) => ({
            id: state.id,
            lastLogin: state.updatedAt,
            name: state.publicProfile?.name ?? state.profile.name,
            photo: state.publicProfile?.photo ?? state.profile.photo,
            species: Pet.getters(state).getCurrentEvolution()?.species,
          }));
      };
      const players: FriendListData[] = [];
      const state = await api.getOwnState();

      const filteredFriends = Object.keys(state.friends);

      // Query offline friends with pets
      const friends = await api.searchPlayers({
        where: {
          isOnline: {
            is: false,
          },
          petSpecies: {
            isNoneOf: [''],
          },
          id: {
            isOneOf: filteredFriends,
          },
        },
        fields: [],
        limit: limitTo,
        sort: 'random',
      });
      players.push(
        ...(await fetchFriendListData(friends.results.map(({ id }) => id))),
      );

      // Don't fetch non-friends unless we have a public profile
      if (state.publicProfile) {
        // Query offline non-friends with pets
        // IMPORTANT!! Non-friends MUST have a public profile configured!
        const nonFriends = await api.searchPlayers({
          where: {
            isOnline: {
              is: false,
            },
            petSpecies: {
              isNoneOf: [''],
            },
            id: {
              isNotOneOf: [state.id, ...Object.keys(state.friends)],
            },
            hasPublicProfile: {
              is: true,
            },
          },
          fields: [],
          limit: limitTo - players.length,
          sort: 'random',
        });
        players.push(
          ...(await fetchFriendListData(
            nonFriends.results.map(({ id }) => id),
          )),
        );
      }

      return players;
    },

    getRecentVisitorList: async (limitTo: number, api) => {
      const state = await api.getOwnState();
      const { visited } = state.visit;
      const now = api.date.now();
      const visitorIds = Object.keys(visited);
      const friendIds = Object.keys(state.friends);

      const visitors: VisitorData[] = [];
      Object.values(await api.fetchStates(visitorIds)).forEach(
        ({ state: visitorState }) => {
          const { publicProfile } = visitorState;
          if (!publicProfile && !friendIds.includes(visitorState.id)) {
            // Can't show private profile data to non-friends
            return;
          }

          const { getCurrentEvolution } = Pet.getters(visitorState);
          const species = getCurrentEvolution()?.species;
          if (!species) {
            // Player doesn't have a current pet to visit
            return;
          }

          visitors.push({
            id: visitorState.id,
            lastLogin: visitorState.updatedAt,
            lastVisit: visited[visitorState.id]?.lastUpdatedAt,
            species,
            ...(publicProfile ?? visitorState.profile),
          });
        },
      );

      return visitors
        .sort((a, b) => b.lastVisit - a.lastVisit)
        .slice(0, limitTo);
    },

    getPlayerVisitDetails: async (playerId: string, api) => {
      const results = await api.fetchStates([playerId]);
      const { state } = results[playerId];

      const {
        getCurrentEvolution,
        getPetAge,
        getAttributeValue,
        getPetState,
      } = Pet.getters(state);
      const species = getCurrentEvolution()?.species;

      // visits/visited swapped because we are checking from the other player's state
      const { getVisitsCount, getVisitedCount } = VisitRecord.getters;
      const userId = api.getUserID();
      const visits = getVisitedCount(state, userId);
      const visited = getVisitsCount(state, userId);

      const now = api.date.now();
      const details: VisitDetailsData = {
        id: playerId,
        name: state.publicProfile?.name,
        photo: state.publicProfile?.photo,
        lastLogin: state.updatedAt,
        isOnline: state.isOnline,
        petName: state.pet.name,
        state: getPetState(now, true),
        species,
        age: getPetAge(now),
        stomach: getAttributeValue('stomach', now),
        love: getAttributeValue('love', now),
        visits,
        visited,
      };

      // If there is no public profile data and the player is a friend,
      // then we can return their private profile info
      if (!details.name && (await api.getOwnState()).friends[playerId]) {
        details.name = state.profile.name;
        details.photo = state.profile.photo;
      }

      return details;
    },

    getCurrentVisitors: async (_: void, api) => {
      const visitors: string[] = [];

      // Fetch visitors in batches of 1000 - we really don't expect to get this many
      // but visiting an online player would create a weird glitchy experience so
      // best to ensure everyone gets kicked out
      const limit = 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { results } = await api.searchPlayers({
          where: {
            id: {
              isNotOneOf: visitors,
            },
            visitingFriendId: {
              isAnyOf: [api.getUserID()],
            },
          },
          fields: [],
          limit,
        });
        visitors.push(...results.map(({ id }) => id));

        if (results.length < limit) {
          break;
        }
      }

      return visitors;
    },
  });
};

type FriendListData = PublicProfile & {
  id: string;
  species: PetSpecies;
  lastLogin: number;
};

export type VisitDetailsData = FriendListData & {
  visits: number;
  visited: number;
  petName: string;
  age: number;
  stomach: number;
  love: number;
  isOnline: boolean;
  state: PetState;
};

type VisitorData = FriendListData & {
  lastVisit: number;
};
